import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Card, Collapse, Descriptions, Space, Spin, Table, Tag, Timeline } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as Component from '../../../../shared/components';
import { formatDateTimePSTWithSeconds } from '../../../../shared/utils/timeFormat';
import { kcpIngestService } from '../service/kcpIngestService';
import {
  ASSET_STATUS_COLORS,
  ContentIngestAsset,
  ContentIngestProcessing,
  INGEST_STATUS_COLORS,
  KCP_TYPE_COLORS,
  KCPIngestDetail as KCPIngestDetailType,
  KCPReportRequest,
} from '../type/kcpIngestType';

interface JsonToggleProps {
  label: string;
  value: unknown;
}

const JsonToggle: React.FC<JsonToggleProps> = ({ label, value }) => {
  const [mode, setMode] = useState<'pretty' | 'raw'>('pretty');

  const formattedValue = useMemo(() => {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'string') {
      if (mode === 'pretty') {
        try {
          return JSON.stringify(JSON.parse(value), null, 2);
        } catch (error) {
          console.error(error);
          return value;
        }
      }
      return value;
    }

    try {
      return JSON.stringify(value, null, mode === 'pretty' ? 2 : 0);
    } catch (error) {
      console.error(error);
      return String(value);
    }
  }, [mode, value]);

  return (
    <div style={{ marginBottom: '12px' }}>
      <Space align="center" size="small" style={{ marginBottom: '4px' }}>
        <strong>{label}</strong>
        <Button
          size="small"
          onClick={() => setMode(prev => (prev === 'pretty' ? 'raw' : 'pretty'))}
          style={{ padding: '0 8px' }}
        >
          {mode === 'pretty' ? 'Raw' : 'Pretty'}
        </Button>
      </Space>
      <pre
        style={{
          background: '#f5f5f5',
          padding: '12px',
          borderRadius: '4px',
          border: '1px solid #d9d9d9',
          maxHeight: '320px',
          overflow: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
        }}
      >
        {formattedValue}
      </pre>
    </div>
  );
};

const KcpIngestDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<KCPIngestDetailType | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadData(parseInt(id, 10));
    }
  }, [id]);

  const loadData = async (contentIngestId: number) => {
    try {
      setLoading(true);
      const result = await kcpIngestService.fetchOne(contentIngestId);
      setData(result);
    } catch (error) {
      console.error('Failed to fetch KCP ingest detail:', error);
    } finally {
      setLoading(false);
    }
  };

  const sortedReportRequests: KCPReportRequest[] = useMemo(() => {
    if (!data?.kcp_report_requests || data.kcp_report_requests.length === 0) {
      return [];
    }

    return [...data.kcp_report_requests].sort(
      (a, b) => new Date(a.requested_at).getTime() - new Date(b.requested_at).getTime()
    );
  }, [data?.kcp_report_requests]);

  if (loading) {
    return (
      <Component.BasePageComponent>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      </Component.BasePageComponent>
    );
  }

  if (!data) {
    return (
      <Component.BasePageComponent>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <h2>데이터를 찾을 수 없습니다.</h2>
          <Button type="primary" onClick={() => navigate('/kcp-ingest')}>
            목록으로 돌아가기
          </Button>
        </div>
      </Component.BasePageComponent>
    );
  }

  return (
    <Component.BasePageComponent>
      <div style={{ padding: '24px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/kcp-ingest')}>
              목록으로
            </Button>
            <h2 style={{ margin: 0 }}>KCP Ingest Detail - CI ID: {data.id}</h2>
            <div style={{ width: '80px' }} />
          </div>

          {/* Basic Information */}
          <Card title="Basic Information" bordered>
            <Descriptions column={2} bordered>
              <Descriptions.Item label="CI ID">{data.id}</Descriptions.Item>
              <Descriptions.Item label="Source">{data.source}</Descriptions.Item>
              <Descriptions.Item label="File Path" span={2}>
                {data.file_path}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={INGEST_STATUS_COLORS[data.status] || 'default'}>
                  {data.status?.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Ingest At">
                {formatDateTimePSTWithSeconds(data.ingest_at)}
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {formatDateTimePSTWithSeconds(data.created)}
              </Descriptions.Item>
              <Descriptions.Item label="Modified">
                {formatDateTimePSTWithSeconds(data.modified)}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* KCP Information */}
          {data.kcp && (
            <Card title="KCP Information" bordered>
              <Descriptions column={2} bordered>
                <Descriptions.Item label="KCP ID">{data.kcp.id}</Descriptions.Item>
                <Descriptions.Item label="Title">{data.kcp.title}</Descriptions.Item>
              </Descriptions>
            </Card>
          )}

          {/* Meta Information */}
          {data.meta && (
            <Card title="Meta Information" bordered>
              <Descriptions column={2} bordered>
                <Descriptions.Item label="Meta ID">{data.meta.id}</Descriptions.Item>
                <Descriptions.Item label="Type">
                  <Tag color={KCP_TYPE_COLORS[data.meta.source_content_type] || 'default'}>
                    {data.meta.source_content_type?.toUpperCase()}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={INGEST_STATUS_COLORS[data.meta.status] || 'default'}>
                    {data.meta.status?.toUpperCase()}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Enabled">
                  {data.meta.enable ? 'Yes' : 'No'}
                </Descriptions.Item>
                <Descriptions.Item label="Target Assets" span={2}>
                  <Space>
                    {data.meta.target_assets?.map(asset => (
                      <Tag key={asset} color="blue">
                        {asset}
                      </Tag>
                    ))}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="Meta Data" span={2}>
                  <pre
                    style={{
                      maxHeight: '400px',
                      overflow: 'auto',
                      background: '#f5f5f5',
                      padding: '12px',
                    }}
                  >
                    {JSON.stringify(data.meta.meta, null, 2)}
                  </pre>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}

          {/* MediaHub Information */}
          {data.mediahub ? (
            <Card title="MediaHub Mapping Information" bordered>
              <Descriptions column={2} bordered>
                <Descriptions.Item label="MediaHub ID">
                  <Link
                    to={
                      data.mediahub.content_type === 'season'
                        ? `/season/edit2/${data.mediahub.id}`
                        : `/episode/edit/${data.mediahub.id}`
                    }
                    style={{ color: '#1890ff' }}
                  >
                    {data.mediahub.id}
                  </Link>
                </Descriptions.Item>
                <Descriptions.Item label="Type">
                  <Tag color="purple">{data.mediahub.content_type?.toUpperCase()}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Slug" span={2}>
                  {data.mediahub.slug}
                </Descriptions.Item>
                <Descriptions.Item label="Publish Status" span={2}>
                  <Tag color="green">{data.mediahub.publish_status?.toUpperCase()}</Tag>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          ) : (
            <Card title="MediaHub Mapping Information" bordered>
              <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                MediaHub 매핑 정보가 없습니다.
              </div>
            </Card>
          )}

          {/* Video Information */}
          {data.video && (
            <Card title="Video Information" bordered>
              <Descriptions column={2} bordered>
                <Descriptions.Item label="Video ID">
                  <Link to={`/assets/edit/${data.video}`} style={{ color: '#1890ff' }}>
                    {data.video}
                  </Link>
                </Descriptions.Item>
                <Descriptions.Item label="Video Asset Status">
                  {data.video_asset_status ? (
                    <Tag color={ASSET_STATUS_COLORS[data.video_asset_status] || 'default'}>
                      {data.video_asset_status.toUpperCase()}
                    </Tag>
                  ) : (
                    '-'
                  )}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}

          {/* Assets Information */}
          <Card title="Assets Processing List" bordered>
            {data.assets && data.assets.length > 0 ? (
              <Table<ContentIngestAsset>
                dataSource={data.assets}
                rowKey="id"
                pagination={false}
                bordered
              >
                <Table.Column<ContentIngestAsset> title="ID" dataIndex="id" key="id" width={80} />
                <Table.Column<ContentIngestAsset>
                  title="Asset Type"
                  dataIndex="asset_type"
                  key="asset_type"
                  render={(assetType: string) => <Tag color="blue">{assetType.toUpperCase()}</Tag>}
                />
                <Table.Column<ContentIngestAsset>
                  title="Status"
                  dataIndex="status"
                  key="status"
                  render={(status: string) => (
                    <Tag color={ASSET_STATUS_COLORS[status] || 'default'}>
                      {status.toUpperCase()}
                    </Tag>
                  )}
                />
                <Table.Column<ContentIngestAsset>
                  title="Created"
                  dataIndex="created"
                  key="created"
                  render={(created: string) => formatDateTimePSTWithSeconds(created)}
                />
                <Table.Column<ContentIngestAsset>
                  title="Modified"
                  dataIndex="modified"
                  key="modified"
                  render={(modified: string) => formatDateTimePSTWithSeconds(modified)}
                />
              </Table>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                Asset 정보가 없습니다.
              </div>
            )}
          </Card>

          {/* KCP Report Debug */}
          <Card title="KCP Report Request/Response (Debug)" bordered>
            <Collapse>
              <Collapse.Panel header="KCP report 요청 로그" key="kcp-report-requests">
                {sortedReportRequests.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '12px 0', color: '#999' }}>
                    로그 없음
                  </div>
                ) : (
                  <Timeline style={{ marginTop: '8px' }}>
                    {sortedReportRequests.map((request, index) => (
                      <Timeline.Item key={`${request.api}-${request.requested_at}-${index}`}>
                        <div style={{ marginBottom: '8px' }}>
                          <Space direction="vertical" size={0}>
                            <Space size="small">
                              <Tag color="purple">API</Tag>
                              <span>{request.api}</span>
                            </Space>
                            <span style={{ color: '#8c8c8c', fontSize: '12px' }}>
                              {/*요청 시각: {formatDateTimePSTWithSeconds(request.requested_at)}*/}
                            </span>
                          </Space>
                        </div>
                        {(() => {
                          const payloadObject = request.payload as
                            | Record<string, unknown>
                            | undefined;
                          const displayPayload = payloadObject?.payload ?? request.payload;
                          const displayHeaders = request.headers ?? payloadObject?.headers ?? null;
                          const displayResult = request.result ?? payloadObject?.result ?? null;

                          return (
                            <>
                              <JsonToggle label="Payload" value={displayPayload} />
                              <JsonToggle label="Headers" value={displayHeaders} />
                              <JsonToggle label="Result" value={displayResult} />
                            </>
                          );
                        })()}
                      </Timeline.Item>
                    ))}
                  </Timeline>
                )}
              </Collapse.Panel>
            </Collapse>
          </Card>

          {/* Processing Content */}
          <Card title="Processing Content (Recent 10)" bordered>
            {data.processing && data.processing.length > 0 ? (
              <Table<ContentIngestProcessing>
                dataSource={data.processing}
                rowKey="id"
                pagination={false}
                bordered
              >
                <Table.Column<ContentIngestProcessing>
                  title="ID"
                  dataIndex="id"
                  key="id"
                  width={80}
                />
                <Table.Column<ContentIngestProcessing>
                  title="Processing"
                  dataIndex="processing"
                  key="processing"
                  render={(processing: string) => (
                    <Tag color="blue">{processing.toUpperCase()}</Tag>
                  )}
                />
                <Table.Column<ContentIngestProcessing>
                  title="Description"
                  dataIndex="description"
                  key="description"
                  render={(description: string | null) => description || '-'}
                />
                <Table.Column<ContentIngestProcessing>
                  title="Created"
                  dataIndex="created"
                  key="created"
                  render={(created: string) => formatDateTimePSTWithSeconds(created)}
                />
              </Table>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                Processing 정보가 없습니다.
              </div>
            )}
          </Card>
        </Space>
      </div>
    </Component.BasePageComponent>
  );
};

export default KcpIngestDetail;
